import { OutreachEditor, OutreachEditorRef, Typography } from '@recruit-robin/robin-theme/web-components';
import { useContext, useEffect, useRef } from 'react';

import type { PublicVacancyResponse } from 'clients/CampaignsClient/CampaignsClient.types';

import {
  EmploymentType,
  getEducationLevelLabel,
  getEmploymentTypeLabel,
  getLanguageLabel,
} from 'shared/legacy/contexts/DialogContext/modals/EnrichVacancyModal/utils/EnrichVacancyModal.constants';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useStyles } from './VacancyDetails.styles';

export const VacancyDetailsContent = ({ vacancy, readonly }: { vacancy: PublicVacancyResponse; readonly: boolean }) => {
  const classes = useStyles({ readonly });
  const { language, dictionary } = useContext(LocalizationContext);
  const outreachEditorRef = useRef<OutreachEditorRef>(null);

  useEffect(() => {
    if (vacancy.job.job_preview) {
      outreachEditorRef.current?.importFromHTML(vacancy.job.job_preview);
    }
  }, [vacancy.job.job_preview]);

  if (vacancy.job.job_preview) {
    return (
      <OutreachEditor.Root customCss={classes.jobPreviewContainer}>
        <OutreachEditor.Message
          ref={outreachEditorRef}
          disabled
          enablePlugin={{
            Attachment: false,
            Link: true,
            List: true,
            PreventTextFormat: false,
            SanitizeTextPlugin: false,
            Tokens: false,
          }}
        />
      </OutreachEditor.Root>
    );
  }

  const translatedEmploymentType = vacancy.job.job_details.employment_type
    ? Array.isArray(vacancy.job.job_details.employment_type)
      ? vacancy.job.job_details.employment_type
          .map((type) => getEmploymentTypeLabel(type as EmploymentType, language))
          .join(', ')
      : vacancy.job.job_details.employment_type
          .split(',')
          .map((type) => type.trim())
          .filter((type) => type)
          .map((type) => getEmploymentTypeLabel(type as EmploymentType, language))
          .join(', ')
    : '';

  return (
    <div css={classes.textContent}>
      <div css={classes.jobDetails}>
        <Typography variant={(v) => v.body.long} color={(c) => c.neutral.variant[70]}>
          {vacancy.job.employment_requirements.address || ''} | {translatedEmploymentType} |{' '}
          {vacancy.job.employment_requirements.working_hours_range || ''} hours
        </Typography>
      </div>

      {vacancy.job.position_overview.description && (
        <TextBlock title={dictionary.aboutTheRole}>{vacancy.job.position_overview.description}</TextBlock>
      )}

      {vacancy.job.responsibilities.key_responsibilities_tasks && (
        <TextBlock title={dictionary.yourResponsibilities}>
          <div>{renderMultilineText(vacancy.job.responsibilities.key_responsibilities_tasks.join('\n'))}</div>
        </TextBlock>
      )}

      {(vacancy.job.requirements.minimum_education_level ||
        vacancy.job.requirements.certificates ||
        vacancy.job.requirements.maximum_language_skills.language ||
        vacancy.job.requirements.drivers_license) && (
        <TextBlock title={dictionary.jobRequirements}>
          <div css={classes.requirements}>
            {vacancy.job.requirements.minimum_education_level && (
              <p>
                {dictionary.education}:{' '}
                {vacancy.job.requirements.minimum_education_level
                  .split(',')
                  .map((level) => level.trim())
                  .filter((level) => level)
                  .map((level) => getEducationLevelLabel(level, language))
                  .join(', ')}
              </p>
            )}
            {vacancy.job.requirements.certificates && (
              <p>
                {dictionary.certificatesLabel}: {vacancy.job.requirements.certificates}
              </p>
            )}
            {vacancy.job.requirements.maximum_language_skills.language && (
              <p>
                {dictionary.language}:{' '}
                {vacancy.job.requirements.maximum_language_skills.language
                  .split(',')
                  .map((lang) => lang.trim())
                  .filter((lang) => lang)
                  .map((lang) => getLanguageLabel(lang, language))
                  .join(', ')}{' '}
                {vacancy.job?.requirements.maximum_language_skills.level &&
                  vacancy.job.requirements.maximum_language_skills.level}
              </p>
            )}
            {vacancy.job.requirements.drivers_license && (
              <p>
                {dictionary.driversLicenseLabel}: {vacancy.job.requirements.drivers_license}
              </p>
            )}
          </div>
        </TextBlock>
      )}

      {vacancy.job.compensation_benefits?.notes && (
        <TextBlock title={dictionary.whatWeOffer}>
          <div>{renderMultilineText(vacancy.job.compensation_benefits.notes.join('\n'))}</div>
        </TextBlock>
      )}

      {vacancy.job.job_details.salary_range && (
        <TextBlock title={dictionary.salary}>
          {formatSalaryRange(vacancy.job.job_details.salary_range, vacancy.job.job_details.salary_unit)}
        </TextBlock>
      )}
    </div>
  );
};

const TextBlock = ({ title, children }: { title: string; children: React.ReactNode }) => {
  const classes = useStyles({});

  return (
    <div css={classes.section}>
      <Typography variant={(v) => v.heading[5]} color={(c) => c.neutral.variant[70]}>
        {title}
      </Typography>
      <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]} css={classes.textContent}>
        {children}
      </Typography>
    </div>
  );
};

const renderMultilineText = (text: string | undefined | null) => {
  if (!text || typeof text !== 'string') return null;

  const lines = text.split('\n').filter((line) => line.trim());

  if (lines.length === 0) return null;

  return (
    <ul style={{ margin: 0, paddingLeft: 20 }}>
      {lines.map((line, index) => (
        <li key={index} style={{ margin: 0 }}>
          {line.trim()}
        </li>
      ))}
    </ul>
  );
};

const formatSalaryRange = (salaryRange: string | undefined | null, salaryUnit?: string | undefined | null) => {
  if (!salaryRange || typeof salaryRange !== 'string') return '';

  if (salaryRange.includes('-')) {
    const [min, max] = salaryRange.split('-').map((val) => val.trim());
    const formattedRange = `€${min} - €${max}`;
    return salaryUnit ? `${formattedRange} ${salaryUnit.toLowerCase()}` : formattedRange;
  }

  const formattedSalary = `€${salaryRange}`;
  return salaryUnit ? `${formattedSalary} ${salaryUnit.toLowerCase()}` : formattedSalary;
};
